<template>
  <CustomModal
    title="Create a follow up task"
    width="500px"
    :visible="visible"
    @update:visible="$emit('update:visible', $event)"
  >
    <div class="flex flex-col gap-6 font-normal proxima_nova">
      <span class="text-sm">
        A follow up task will be created for the below selected date & time
      </span>

      <div id="task_name" class="flex flex-col gap-1">
        <span class="text-[13px] secondary_color">Task Name</span>
        <a-input
          v-model:value="taskForm.title"
          placeholder="Enter task name"
          class="input_medium"
          allowClear
        />
      </div>

      <div id="create_time" class="flex flex-col gap-2">
        <span class="text-[13px] secondary_color">Due Date and Time</span>

        <div class="flex flex-col gap-4 w-full">
          <a-radio-group
            v-model:value="selectedTimeOption"
            class="flex flex-col !gap-2 w-full"
          >
            <a-radio
              v-for="(option, index) in timeOptions"
              :key="`${option.value}_option_${index}`"
              :value="option.value"
              class="!flex !items-start w-full"
            >
              <div
                class="flex flex-row w-full items-center justify-between ml-2"
              >
                <span class="text-sm font-normal primary_color">{{
                  option.label
                }}</span>

                <span
                  v-if="option.dateTime"
                  class="text-[13px] opacity-60 secondary_color"
                  >{{ option.dateTime }}</span
                >
              </div>
            </a-radio>
          </a-radio-group>
        </div>
      </div>

      <CustomDateTimeFilter
        v-if="selectedTimeOption === 'custom'"
        source="follow_up_task_modal"
        v-model:start-date="customDate"
        v-model:end-date="customTime"
      />
    </div>

    <template #footer>
      <div class="flex flex-row gap-2 justify-between w-full">
        <a-button class="secondary_medium" @click="handleClose"
          >Cancel</a-button
        >

        <a-button
          class="primary_medium"
          @click="handleCreateTask"
          :loading="loading"
          :disabled="!taskForm.title?.trim()"
        >
          {{ loading ? "Creating.." : "Create task" }}
        </a-button>
      </div>
    </template>
  </CustomModal>
</template>

<script setup>
import { ref, watch } from "vue";
import dayjs from "dayjs";
import { message } from "ant-design-vue";
import {
  getInitialDateTime,
  getSelectedDateTime,
  getTimeOptions,
} from "@/src/utils/dateTimeUtils.js";
import DashboardService from "@/src/api/DashboardService.js";
import CustomModal from "@/src/components/CustomModal.vue";
import CustomDateTimeFilter from "@/src/components/custom_components/CustomDateTimeFilter.vue";

const props = defineProps({
  visible: { type: Boolean, default: false },
  parentTask: { type: Object, default: null },
});

const emit = defineEmits(["update:visible", "task-created", "close"]);

const loading = ref(false);
const customDate = ref(null);
const customTime = ref(null);
const selectedTimeOption = ref("in_3_days");

const taskForm = ref({
  title: "",
  email_reminder: "No Reminder",
  reminder_date: null,
  reminder_time: null,
});

const timeOptions = ref(getTimeOptions());

const resetState = () => {
  taskForm.value.title = "";
  taskForm.value.email_reminder = "No Reminder";
  taskForm.value.reminder_date = null;
  taskForm.value.reminder_time = null;
  selectedTimeOption.value = "in_3_days";
  customDate.value = null;
  customTime.value = null;
};

watch(
  () => props.visible,
  (newVal) => {
    if (newVal && props.parentTask) {
      taskForm.value.title = `Follow up task: "${props.parentTask.title}"`;
    }
    if (newVal) {
      timeOptions.value = getTimeOptions();
      initializeDefaultDateTime();
    }
  },
);

const initializeDefaultDateTime = () => {
  const { due_date, due_time } = getInitialDateTime();
  customDate.value = due_date;
  customTime.value = due_time;
  taskForm.value.reminder_date = due_date;
  taskForm.value.reminder_time = due_time;
};

// Convert date from DD/MM/YYYY to YYYY-MM-DD (ISO format)
const formatDateToISO = (dateStr) => {
  if (!dateStr) return null;
  // Check if already in ISO format (YYYY-MM-DD)
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    return dateStr;
  }
  // Parse DD/MM/YYYY format
  const match = dateStr.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (match) {
    const [, day, month, year] = match;
    return `${year}-${month}-${day}`;
  }
  // Try parsing with dayjs as fallback
  const parsed = dayjs(dateStr, ['DD/MM/YYYY', 'YYYY-MM-DD', 'MM/DD/YYYY']);
  if (parsed.isValid()) {
    return parsed.format('YYYY-MM-DD');
  }
  return dateStr;
};

// Convert time to 12-hour format with uppercase AM/PM (e.g., "11:30am" -> "11:30 AM")
const formatTimeTo12Hr = (timeStr) => {
  if (!timeStr) return null;
  // Check if already in correct format (with space and uppercase AM/PM)
  if (/^\d{1,2}:\d{2}\s?(AM|PM)$/.test(timeStr)) {
    return timeStr;
  }
  // Parse h:mma or h:mm am format and convert
  const match = timeStr.match(/^(\d{1,2}):(\d{2})\s?(am|pm|AM|PM)$/i);
  if (match) {
    const [, hours, minutes, period] = match;
    return `${hours}:${minutes} ${period.toUpperCase()}`;
  }
  return timeStr;
};

const buildTaskPayload = (taskData) => {
  const payload = {
    title: taskData.title,
    due_date: formatDateToISO(taskData.due_date),
    due_time: formatTimeTo12Hr(taskData.due_time),
    status: taskData.status,
    priority: taskData.priority,
    email_reminder: taskData.email_reminder,
  };

  // Add custom reminder fields if applicable
  if (taskData.reminder_date) {
    payload.reminder_date = taskData.reminder_date;
  }
  if (taskData.reminder_time) {
    payload.reminder_time = taskData.reminder_time;
  }

  // Handle assigned_to (single user)
  if (taskData.assigned_to != null) {
    const assignedToValue = typeof taskData.assigned_to === 'object' 
      ? taskData.assigned_to.value 
      : taskData.assigned_to;
    if (assignedToValue != null) {
      payload.assignee_ids = [assignedToValue];
    }
  }

  // Copy entity associations directly (TasksController expects these as arrays)
  if (Array.isArray(taskData.lead_ids) && taskData.lead_ids.length > 0) {
    payload.lead_ids = taskData.lead_ids;
  }
  if (Array.isArray(taskData.contact_ids) && taskData.contact_ids.length > 0) {
    payload.contact_ids = taskData.contact_ids;
  }
  if (Array.isArray(taskData.company_ids) && taskData.company_ids.length > 0) {
    payload.company_ids = taskData.company_ids;
  }
  if (Array.isArray(taskData.deal_ids) && taskData.deal_ids.length > 0) {
    payload.deal_ids = taskData.deal_ids;
  }

  return payload;
};

const handleCreateTask = async () => {
  if (!taskForm.value.title.trim()) {
    message.error("Please enter a task name");
    return;
  }

  const dateTime = getSelectedDateTime(
    selectedTimeOption.value,
    customDate.value,
    customTime.value,
  );
  if (!dateTime) {
    message.error("Please select a valid date and time");
    return;
  }

  loading.value = true;
  try {
    const taskData = {
      title: taskForm.value.title,
      due_date: dateTime.date,
      due_time: dateTime.time,
      status: "Todo",
      priority: "Medium",
      email_reminder: taskForm.value.email_reminder,
      ...(taskForm.value.email_reminder === "custom_date" &&
        taskForm.value.reminder_date &&
        taskForm.value.reminder_time && {
          reminder_date: taskForm.value.reminder_date.format("YYYY-MM-DD"),
          reminder_time: taskForm.value.reminder_time,
        }),
    };

    // Fetch parent task details for associations and assigned_to
    if (props.parentTask?.id) {
      try {
        const taskDetailsResponse = await DashboardService.getTask(props.parentTask.id);
        if (taskDetailsResponse?.success && taskDetailsResponse.task) {
          const parentTaskDetails = taskDetailsResponse.task;
          
          // Copy entity associations from fetched parent task details
          if (parentTaskDetails.lead_ids?.length > 0) {
            taskData.lead_ids = parentTaskDetails.lead_ids;
          }
          if (parentTaskDetails.contact_ids?.length > 0) {
            taskData.contact_ids = parentTaskDetails.contact_ids;
          }
          if (parentTaskDetails.company_ids?.length > 0) {
            taskData.company_ids = parentTaskDetails.company_ids;
          }
          if (parentTaskDetails.deal_ids?.length > 0) {
            taskData.deal_ids = parentTaskDetails.deal_ids;
          }
          
          // Copy assigned_to
          if (parentTaskDetails.assigned_to?.value) {
            taskData.assigned_to = parentTaskDetails.assigned_to.value;
          }
        }
      } catch (error) {
        console.error("Error fetching parent task details:", error);
      }
    }

    // Build task payload for TasksController
    const taskPayload = buildTaskPayload(taskData);

    // Use DashboardService.createTask which calls POST /api/tasks
    // This endpoint handles entity associations (contact_ids, lead_ids, etc.) correctly
    const response = await DashboardService.createTask(taskPayload);

    if (response?.success) {
      message.success("Follow-up task created successfully");
      emit("task-created", response.task);
      handleClose();
    } else {
      message.error(response?.errors?.[0] || response?.message || "Failed to create follow-up task");
    }
  } catch (error) {
    console.error("Error creating follow-up task:", error);
    const errorMessage = error?.response?.data?.errors?.[0] 
      || error?.response?.data?.message 
      || error?.message 
      || "An error occurred while creating the follow-up task";
    message.error(errorMessage);
  } finally {
    loading.value = false;
  }
};

const handleClose = () => {
  emit("update:visible", false);
  emit("close");
  resetState();
};
</script>

<style scoped>
:deep(.ant-radio-wrapper) {
  display: flex !important;
  align-items: flex-start !important;
  width: 100% !important;
  margin: 8px 0 !important;
}

:deep(.ant-radio) {
  margin-top: 2px;
}

:deep(.ant-radio + *) {
  flex: 1;
  padding-top: 2px;
}
</style>
